import { BaseView } from "./BaseView";
import React, { useEffect } from "react";
import { Input } from "../components/ui/input";
import { BellOffIcon, SearchIcon, RefreshCcw, AlertTriangleIcon } from "lucide-react";
import axios from "../utility/customAxios";
import { Button } from "../components/ui/button";
import { AiOutlineKubernetes } from "react-icons/ai";
import { FaSlack } from "react-icons/fa";
import { usePreserveQueryParamsNavigate } from "../components/ui/lib/utils";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../components/ui/tooltip";
import { withErrorHandling } from "utility/apiUtils";
import { toast } from "../components/ui/use-toast";
import { DataTable, ColumnConfig } from "../components/ui/datatable/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { AlertFiresTimeline } from "../components/AlertFiresTimeline";
import { useSelector } from "react-redux";
import timerange from "../store/reducers/timerange";

function AlertSearchBar(props: {
    search: string,
    setSearch: (value: (((prevState: string) => string) | string)) => void,
    refreshAlerts: () => void
}) {
    const navigate = usePreserveQueryParamsNavigate()

    return <div className={"flex grow h-12 gap-2"}>
        <div
            className={"bg-backgroundmedium flex-none grow h-12 px-[7px] py-[9px] focus-within:border-primary hover:cursor-pointer hover:border-primary rounded border justify-between items-center gap-2 inline-flex"}>
            <Input
                placeholder={"Search for alerts"}
                className={"bg-transparent focus-visible:ring-0 border-none border-0 ring-0 outline-0 text-textmedium"}
                value={props.search} onChange={(e) => {
                    props.setSearch(e.target.value)
                }} />
            <SearchIcon className={"text-textdark"} />
        </div>
        <div className={"flex gap-2"}>
            <Button className={"h-12 px-4 bg-secondarytransparenter border border-secondary text-textmedium rounded"}
                onClick={() => {
                    navigate("/new-alert")
                }}
                requiredPermission={{
                    operation: "create",
                    resource: "alerts/default/*"
                }}
            >Create Alert</Button>
        </div>
    </div>
}

function AlertSearchResults(props: { alerts: AlertSearchResult[], isLoading?: boolean }) {
    const navigate = usePreserveQueryParamsNavigate()

    // Define columns for the DataTable
    // Function to handle row click
    const handleRowClick = (alert: AlertSearchResult, e: React.MouseEvent) => {
        // If middle mouse button or cmd/ctrl + click, open in new tab
        if (e.button === 1 || (e.ctrlKey || e.metaKey)) {
            window.open(`/alert?alertId=${alert.uuid}`, '_blank');
        } else {
            navigate(`/alert?alertId=${alert.uuid}`);
        }
    };

    const columns: ColumnDef<AlertSearchResult>[] = [
        {
            id: "name",
            header: "Name",
            cell: ({ row }) => {
                const alert = row.original;
                const isOutOfSync = alert.alertManagement &&
                    alert.alertManagement.managingResourceType &&
                    (!alert.alertManagement.lastSyncedTime ||
                        (Date.now() / 1000 - alert.alertManagement.lastSyncedTime) > 6 * 60 * 60);

                return (
                    <div 
                        className="flex items-center gap-2 cursor-pointer py-1"
                        onClick={(e) => handleRowClick(alert, e)}
                    >
                        {alert.muted ?
                            <Tooltip delayDuration={10}>
                                <TooltipTrigger>
                                    <BellOffIcon className={"w-5 h-5 text-amber-500"} />
                                </TooltipTrigger>
                                <TooltipContent side={"right"} className={"font-semibold bg-backgroundlight text-textmedium text-sm"}>This alert is muted, you will not get notified when it fires. </TooltipContent>
                            </Tooltip>
                            : isOutOfSync ?
                                <Tooltip delayDuration={10}>
                                    <TooltipTrigger>
                                        <AlertTriangleIcon className={"w-5 h-5 text-red-500"} />
                                    </TooltipTrigger>
                                    <TooltipContent side={"right"} className={"font-semibold bg-backgroundlight text-textmedium text-sm"}>This managed alert hasn't been synced in over 6 hours.</TooltipContent>
                                </Tooltip>
                                : <div className={"w-5 h-5"}></div>}
                        <div className="font-normal text-textmedium line-clamp-2">
                            {alert.name}
                        </div>
                    </div>
                );
            },
        },
        {
            id: "status",
            header: "Status",
            accessorKey: "status",
            filterFn: (row, id, value) => {
                return value.includes(row.getValue(id))
            },

            cell: ({ row }) => {
                const alert = row.original;
                const isOutOfSync = alert.alertManagement &&
                    alert.alertManagement.managingResourceType &&
                    (!alert.alertManagement.lastSyncedTime ||
                        (Date.now() / 1000 - alert.alertManagement.lastSyncedTime) > 6 * 60 * 60);

                return (
                    <div 
                        className="flex items-center font-normal leading-8 text-white cursor-pointer text-nowrap"
                        onClick={(e) => handleRowClick(alert, e)}
                    >
                        {isOutOfSync ? 
                            <div className="inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 rounded bg-red-500">Out of Sync</div>
                            : alert.status === "FIRING" ? 
                                <div className="inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 rounded bg-red-500">Firing</div>
                                :
                                <div className="inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 rounded bg-secondaryTransparent">Ok</div>
                        }
                    </div>
                );
            },
        },
        // {
        //     id: "type",
        //     header: "Type",
        //     cell: ({ row }) => {
        //         const alert = row.original;
        //         return (
        //             <div
        //                 className="flex items-center font-normal leading-8 text-white cursor-pointer"
        //                 onClick={(e) => handleRowClick(alert, e)}
        //             >
        //                 {alert.type === "Kubernetes" && <div
        //                     className="inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 rounded bg-blue-500 gap-2">
        //                     <AiOutlineKubernetes className={"text-white h-4 w-4"} />Kubernetes
        //                 </div>}
        //                 {alert.type === "Log" && <div
        //                     className="inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 bg-blue-500 rounded">
        //                     Log</div>}
        //                 {alert.type === "Metric" && <div
        //                     className="inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 bg-blue-500 rounded">
        //                     Metric</div>}
        //                 {alert.type === "Trace" && <div
        //                     className="inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 bg-blue-500 rounded">
        //                     Trace</div>}
        //                 {alert.type === "KubernetesResource" && <div
        //                     className="inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 rounded bg-blue-500 gap-2">
        //                     <AiOutlineKubernetes className={"text-white h-4 w-4"} />Kubernetes
        //                 </div>}
        //                 {alert.type === "MultiMetric" && <div
        //                     className="inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 bg-blue-500 rounded">
        //                     Multi Metric</div>}
        //             </div>
        //         );
        //     },
        // },
        {
            id: "destination",
            header: "Destination",
            cell: ({ row }) => {
                const alert = row.original;
                return (
                    <div 
                        className="flex items-center font-normal leading-8 text-white cursor-pointer"
                        onClick={(e) => handleRowClick(alert, e)}
                    >
                        {alert.destinations.length === 0 ? (
                            <div className="text-sm text-textdark text-start">No destinations</div>
                        ) : (
                            <div className={"flex flex-row gap-2 items-center h-max"}>
                                {/* Always show the first destination as a badge */}
                                <div className="h-full flex-none items-center font-normal font-['Inter'] leading-8 text-white">
                                    {alert.destinations[0] === "Slack" && <div
                                        className="flex w-max justify-center items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 rounded bg-purple-500 gap-2">
                                        <FaSlack className={"text-white h-4 w-4"} />Slack
                                    </div>}
                                    {alert.destinations[0] === "PagerDuty" && <div
                                        className="flex w-max justify-center items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 rounded bg-[#06AC38] gap-2">
                                        PagerDuty
                                    </div>}
                                    {alert.destinations[0] === "Email" && <div
                                        className="flex w-max justify-center items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 rounded bg-red-400 gap-2">
                                        Email
                                    </div>}
                                    {alert.destinations[0] === "Webhook" && <div
                                        className="flex w-max justify-center items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-primary-foreground shadow hover:bg-primary/80 rounded bg-blue-500 gap-2">
                                        Webhook
                                    </div>}
                                </div>

                                {/* Show +N circle if there are additional destinations */}
                                {alert.destinations.length > 1 && (
                                    <TooltipProvider delayDuration={10}>
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <div
                                                    className="flex w-[22px] h-[22px] justify-center items-center rounded bg-primarytransparent border border-primary text-textmedium text-xs font-semibold pr-[2px]">
                                                    <div
                                                        className={"flex flex-col justify-center"}>+{alert.destinations.length - 1}</div>
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <div className="flex flex-col gap-1">
                                                    {alert.destinations.slice(1).map((destination, index) => (
                                                        <div key={index} className="text-sm text-white">
                                                            {destination}
                                                        </div>
                                                    ))}
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            id: "managedBy",
            header: "Managed By",
            cell: ({ row }) => {
                const alert = row.original;
                return (
                    <div 
                        className="flex items-center cursor-pointer text-nowrap"
                        onClick={(e) => handleRowClick(alert, e)}
                    >
                        <Tooltip delayDuration={10}>
                            <TooltipTrigger>
                                <div
                                    className={`inline-flex items-center gap-1`}>
                                    {alert.alertManagement && alert.alertManagement.managingResourceType ? alert.alertManagement.managingResourceType : "Metoro UI"}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side={"right"} className={"font-semibold bg-backgroundlight text-textmedium text-sm"}>
                                {alert.alertManagement && alert.alertManagement.managingResourceIdentifier ? alert.alertManagement.managingResourceIdentifier : "This alert is managed by the Metoro UI"}
                            </TooltipContent>
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            id: "description",
            header: "Description",
            cell: ({ row }) => {
                const alert = row.original;
                return (
                    <div 
                        className="font-normal text-textmedium line-clamp-2 max-[975px]:hidden cursor-pointer"
                        onClick={(e) => handleRowClick(alert, e)}
                    >
                        {alert.description}
                    </div>
                );
            },
        },
    ];

    return (
        <div className="mt-2 min-w-0 w-full flex flex-col grow shrink">
            <DataTable
                columns={columns}
                data={props.alerts}
                tableTitle={"Alerts"}
                filters={[
                    { filteringColumn: "status", title: "Status" }
                ]}
                showPagination={true}
                defaultPageSize={50}
                isLoading={props.isLoading}
                emptyStateMessage={
                    <div className={"flex grow shrink flex-col gap-4 justify-center text-textdark"}>
                        <div className={"flex justify-center items-center text-2xl text-center"}>No alerts found</div>
                        <div className={"flex justify-center items-center text-lg"}>To create a new alert, use the Create Alert button in the top right.</div>
                    </div>
                }
                className="overflow-y-auto"
            />
        </div>
    );
}

interface AlertSearchResult {
    uuid: string
    name: string
    description: string
    status: string
    type: string
    destinations: string[]
    muted: boolean
    alertManagement?: {
        managingResourceType?: string,
        managingResourceIdentifier?: string,
        lastSyncedTime?: number,
    }
}

function AlertsComponent() {
    const [search, setSearch] = React.useState<string>("")
    const [alerts, setAlerts] = React.useState<AlertSearchResult[]>([])
    const [isLoading, setIsLoading] = React.useState(true)
    const timeRange = useSelector(timerange.selectors.getTimeRange)

    const fetchAlerts = React.useCallback(() => {
        setIsLoading(true)
        let searchParams = ""
        if (search) {
            searchParams = `?query=${search}`
        }
        withErrorHandling(async () => {
            const response = await axios.get(`/api/v1/searchAlerts${searchParams}`)
            // Sort alerts with FIRING status at the top, then alphabetically by name
            const sortedAlerts = [...response.data.alerts].sort((a, b) => {
                // First, prioritize FIRING alerts
                if (a.status === "FIRING" && b.status !== "FIRING") return -1;
                if (a.status !== "FIRING" && b.status === "FIRING") return 1;
                // Then sort alphabetically by name
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            });
            setAlerts(sortedAlerts);
        }).catch((error) => {
            console.error(error)
        }).finally(() => {
            setIsLoading(false)
        })
    }, [search])

    useEffect(() => {
        fetchAlerts()
    }, [fetchAlerts])

    // Get time range from selector
    const [startTime, endTime] = timeRange.getStartEnd();
    const startTimeSeconds = startTime.getTime() / 1000;
    const endTimeSeconds = endTime.getTime() / 1000;

    return <div className={"flex flex-col grow shrink p-2 overflow-y-auto"}>
        <div className={"h-max"}>
            <AlertSearchBar search={search} setSearch={setSearch} refreshAlerts={fetchAlerts} />
        </div>
        <AlertFiresTimeline startTime={startTimeSeconds} endTime={endTimeSeconds} className={"mt-2"} />
        <div className={"flex grow shrink min-h-0"}>
            <AlertSearchResults alerts={alerts} isLoading={isLoading} />
        </div>
    </div>

}

const Alerts = () => {
    return <BaseView title={"Alerts"} disableClusterSelector={true} showSyncAlertsButton={true}>
        <AlertsComponent />
    </BaseView>
}


export {
    Alerts
}
